import React, { useState, useMemo } from 'react';
import { CellProps } from 'react-table';
import Breadcrumbs from '~/components/Breadcrumbs/Breadcrumbs';
import CobuTableAdvanced from '~/components/CobuTableAdvanced/CobuTableAdvanced';
import Spacer from '~/components/Spacer/Spacer';
import CobuConstants from '~/helpers/cobuConstants';
import GenericModal from '~/components/GenericModal/GenericModal';
import useManageWords from '~/hooks/managewords/useManageWords';
import { ModeratedWord } from '~/types/manageWords';
import ManageWordForm from './ManageWordsForm';
import styles from './ManageWordsList.module.css';

interface ManageWordsListProps {
  manageWords: ReturnType<typeof useManageWords>;
}

export type WordCategory = 'forbidden' | 'moderated';

type CurrentWord = ModeratedWord & {
  type: WordCategory;
};

export default function ManageWordsList({ manageWords }: ManageWordsListProps) {
  const { forbiddenWords, moderatedWords, createWord, updateWord, deleteWord } =
    manageWords;
  const [currentWord, setCurrentWord] = useState<CurrentWord | null>(null);
  const [createModalType, setCreateModalType] = useState<WordCategory | null>(
    null
  );

  const tableConfig = useMemo(() => ({
    forbidden: getTableConfig('forbidden'),
    moderated: getTableConfig('moderated')
  }), []);

  return (
    <div className='content-container'>
      <Breadcrumbs />
      <Spacer height={20} />
      <h1>{CobuConstants.manageWords.title}</h1>
      <Spacer height={20} />
      {getManageWordsTables()}
      {getCreateWordModal()}
      {getEditWordModal()}
    </div>
  );

  function getManageWordsTables() {
    return (
      <div className={styles.container}>
        <div className={styles.tableContainer}>
          {getCobuTable(forbiddenWords, 'forbidden')}
        </div>
        <div className={styles.tableContainer}>
          {getCobuTable(moderatedWords, 'moderated')}
        </div>
      </div>
    );
  }

  function getCobuTable(data: ModeratedWord[], type: WordCategory) {
    const action =
      type === 'forbidden'
        ? CobuConstants.manageWords.createForbiddenWord
        : CobuConstants.manageWords.createModeratedWord;
    const title =
      type === 'forbidden'
        ? CobuConstants.manageWords.forbiddenWords
        : CobuConstants.manageWords.moderatedWords;
    return (
      <div>
        <div className={styles.tableHeader}>
          <h3>{title}</h3>
          <div className='margin-left-16'>
            <button
              className='btn-primary btn-standard bold'
              onClick={() => setCreateModalType(type)}
              aria-label={`Create new ${type} word`}
            >
              {action}
            </button>
          </div>
        </div>
        <CobuTableAdvanced
          columns={tableConfig[type]}
          data={data}
          noData={{ label: action }}
          actionsStyle={{ top: '130px' }}
        />
      </div>
    );
  }

  function getTableConfig(type: WordCategory) {
    return [
      {
        Header: 'TERM',
        accessor: 'term',
        Cell: (cell: CellProps<ModeratedWord>) => (
          <div className={`${styles.tableContent} default-text`}>
            {cell.row.original.term}
          </div>
        )
      },
      {
        id: 'delete',
        Cell: (cell: CellProps<ModeratedWord>) => (
          <div
            className={'more-actions-btn more-actions-btn-unselected pointer'}
            onClick={() => deleteWord(type, cell.row.original.uuid)}
            role="button"
            tabIndex={0}
            aria-label={`Delete ${type} word: ${cell.row.original.term}`}
          >
            {CobuConstants.manageWords.delete}
          </div>
        )
      },
      {
        id: 'edit',
        Cell: (cell: CellProps<ModeratedWord>) => (
          <div
            className={'more-actions-btn more-actions-btn-unselected pointer'}
            onClick={() => {
              setCurrentWord({
                type,
                ...cell.row.original
              });
            }}
            role="button"
            tabIndex={0}
            aria-label={`Edit ${type} word: ${cell.row.original.term}`}
          >
            {CobuConstants.manageWords.edit}
          </div>
        )
      }
    ];
  }

  function getEditWordModal() {
    if (!currentWord) return null;

    return (
      <GenericModal
        showModal={true}
        title={getTitle()}
        content={
          <ManageWordForm
            isUpdate={true}
            onPressCancel={onPressCancel}
            term={currentWord.term}
            onSubmit={onSubmit}
          />
        }
        hideActionButton
        canceActionButton
        actionText={''}
        actionFunction={() => {}}
        onChange={() => {}}
      />
    );

    function onPressCancel() {
      setCurrentWord(null);
    }
    
    function getTitle() {
      return currentWord?.type === 'forbidden'
        ? CobuConstants.manageWords.updateForbiddenWord
        : CobuConstants.manageWords.updateModeratedWord;
    }

    async function onSubmit(term: string) {
      await updateWord(
        term,
        currentWord?.type as WordCategory,
        currentWord?.uuid as string
      );

      setCurrentWord(null);
    }
  }

  function getCreateWordModal() {
    if (!createModalType) return null;

    return (
      <GenericModal
        showModal={true}
        title={getTitle()}
        content={
          <ManageWordForm
            onPressCancel={onPressCancel}
            onSubmit={onSubmit}
          />
        }
        hideActionButton
        canceActionButton
        actionText={''}
        actionFunction={() => {}}
        onChange={() => {}}
      />
    );

    function onPressCancel() {
      setCreateModalType(null);
    }

    async function onSubmit(term: string) {
      await createWord(term, createModalType as WordCategory);
      setCreateModalType(null);
    }

    function getTitle() {
      return createModalType === 'forbidden'
        ? CobuConstants.manageWords.createForbiddenWord
        : CobuConstants.manageWords.createModeratedWord;
    }
  }
}
